import { Action, NodeData } from "@/models/flows";
import { useFlowStore } from "@/store/flow";
import { getActionBasedOnType } from "@/utils/flows";
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MiniMap,
  Node,
  Position,
  ReactFlow,
} from "@xyflow/react";
import {
  CirclePlay,
  CirclePlayIcon,
  Copy,
  LayoutTemplateIcon,
  PlusIcon,
  Trash,
} from "lucide-react";
import { useMemo, MouseEvent, useState, useEffect } from "react";
import clsx from "clsx";
import { NodeLayout } from "@/components/dashboard/flows/NodeLayout";

export const FlowRenderer = () => {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    activeNode,
    setActiveNode,
    isChoosingNode,
    setIsChoosingNode,
  } = useFlowStore();

  const colorMap: Record<string, string> = {
    green: "bg-green-400/10 ring-green-400/60 ring-offset-green-500",
    blue: "bg-blue-400/10 ring-blue-400/60 ring-offset-blue-500",
    yellow: "bg-yellow-400/10 ring-yellow-400/60 ring-offset-yellow-500",
    purple:
      "bg-purple-400/10 ring-purple-400/60 ring-offset-purple-500 text-purple-400",
  };

  const StarterNode = ({ id, data }: { id: string; data: NodeData }) => {
    const { edges, activeNode } = useFlowStore();
    const action: Action | undefined = useMemo(
      () => getActionBasedOnType(data.type),
      [data.type]
    );

    const hasConnection = edges.some((edge) => edge.source === id);

    return (
      <NodeLayout
        id={id}
        color={action?.colour || "red"}
        hasConnection={hasConnection}
        isTrigger
      >
        <div
          className={`-top-[1.9rem] left-0 pb-1 absolute flex items-center text-${
            action?.colour
          }-400 ${clsx(
            "ring-1 ring-inset rounded-b-lg rounded-tr-lg",
            colorMap[action?.colour || "red"],
            "p-2 flex",
            activeNode && activeNode.id === id && "ring-offset-2"
          )} px-2 rounded-t-md pt-1`}
        >
          <CirclePlayIcon className="w-4 h-4 mr-1" />
          <span className="text-xs mr-1 dark:text-white">Trigger</span>
        </div>
        {action && (
          <div className={`p-1.5 bg-${action.colour}-400 rounded-lg`}>
            <action.icon className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="ml-2 flex items-center">
          <div>
            <h3
              className={`uppercase text-[0.5rem] text-${action?.colour}-400`}
            >
              {data?.category}
            </h3>
            <h1 className="text-[0.8rem] text-white/80 leading-tight">
              {action?.label}
            </h1>
          </div>
        </div>
      </NodeLayout>
    );
  };

  const RegularNode = ({ id, data }: { id: string; data: NodeData }) => {
    const { edges } = useFlowStore();
    const action: Action | undefined = useMemo(
      () => getActionBasedOnType(data.type),
      [data.type]
    );

    const hasConnection = edges.some((edge) => edge.source === id);

    return (
      <NodeLayout
        id={id}
        color={action?.colour || "red"}
        hasConnection={hasConnection}
      >
        {action && (
          <div className={`p-1.5 bg-${action.colour}-400 rounded-lg`}>
            <action.icon className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="ml-2 flex items-center">
          <div>
            <h3
              className={`uppercase text-[0.5rem] text-${action?.colour}-400`}
            >
              {data?.category}
            </h3>
            <h1 className="text-[0.8rem] text-white/80 leading-tight">
              {action?.label}
            </h1>
          </div>
        </div>
      </NodeLayout>
    );
  };

  const EmptyNode = ({ id, data }: { id: string; data: NodeData }) => {
    return (
      <div>
        {/* Top handle */}
        <Handle id="source" type="target" position={Position.Top} />
        <div className="relative inline-flex items-center px-10 py-6 bg-zinc-900 text-white rounded-xl cursor-pointer">
          <div className="absolute inset-0 border border-blue-500/80 rounded-xl"></div>
          <div className="absolute inset-[0.2rem] border border-dashed border-blue-500/20 rounded-lg"></div>
          <div className="relative flex items-center space-x-2 dark:text-white/30">
            <PlusIcon className="w-4 h-4" />
            <span className="text-sm">Pick action type in sidebar</span>
          </div>
        </div>
      </div>
    );
  };

  const nodeTypes = useMemo(
    () => ({
      starter: StarterNode,
      regular: RegularNode,
      empty: EmptyNode,
    }),
    // re-init when anything in the node changes
    []
  );

  const onNodeClick = (event: MouseEvent, node: Node) => {
    setActiveNode(node);
  };

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      nodeTypes={nodeTypes}
      onNodeClick={onNodeClick}
      defaultEdgeOptions={{ type: "default" }} // Set default edge type to "default"
      className="relative"
      zoomOnScroll={nodes.length > 0}
      zoomOnPinch={nodes.length > 0}
      zoomOnDoubleClick={nodes.length > 0}
    >
      {nodes.length == 0 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 top-60 mx-auto z-40">
          <div className="relative inline-flex items-center px-10 py-6 bg-zinc-900 text-white rounded-xl cursor-pointer">
            <div className="absolute inset-0 border border-blue-500/80 rounded-xl"></div>
            <div className="absolute inset-[0.2rem] border border-dashed border-blue-500/20 rounded-lg"></div>
            <div className="relative flex items-center space-x-2 dark:text-white/30">
              <CirclePlayIcon className="w-4 h-4" />
              <span className="text-sm">Set a trigger in the sidebar</span>
            </div>
          </div>
          <div className="flex items-center justify-center my-2 mx-auto">
            <div className="w-full border-t border-gray-600 opacity-30"></div>
            <span className="mx-3 text-gray-500 opacity-60">OR</span>
            <div className="w-full border-t border-gray-600 opacity-30"></div>
          </div>
          <div className="flex items-center justify-center my-2 mx-auto">
            <div className="relative inline-flex items-center px-8 py-3 bg-zinc-900 text-white rounded-xl border dark:border-white/10 border-dashed cursor-pointer hover:border-blue-500/80">
              <div className="relative flex items-center space-x-2 dark:text-white/70">
                <LayoutTemplateIcon className="w-4 h-4" />
                <span className="text-sm">Start with a template</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <Controls />
      <MiniMap />
      <Background variant={BackgroundVariant.Dots} gap={10} size={1} />
    </ReactFlow>
  );
};
